import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Printer } from "lucide-react";
import { BRAND_LOGO_URL } from "@/lib/brand";

export default function JobPrint() {
  const { id } = useParams();
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data));
  }, [id]);

  if (!job)
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento…</div>;

  const totalItems = job.items.reduce((s, i) => s + (i.quantity || 1), 0);

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white py-6 print:py-0">
      <div className="max-w-4xl mx-auto bg-white print:shadow-none shadow-xl rounded-2xl print:rounded-none print:p-10 p-10 print-page">
        <div className="no-print flex items-center justify-end mb-6">
          <button
            onClick={() => window.print()}
            data-testid="print-trigger-button"
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-md"
          >
            <Printer className="w-4 h-4" /> Stampa
          </button>
        </div>

        {/* Header */}
        <header className="flex items-start justify-between pb-6 border-b-2 border-slate-900">
          <div className="flex items-center gap-4">
            <img src={BRAND_LOGO_URL} alt="In & Out Traslochi" className="h-16 w-auto object-contain" />
            <div className="pl-4 border-l border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Foglio di Lavoro
              </div>
              <div className="font-mono text-xs text-slate-400 mt-1">In & Out Traslochi</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-xs text-slate-500">RIF.</div>
            <div className="font-mono font-bold text-slate-900">#{job.id.slice(0, 8).toUpperCase()}</div>
            <div className="mt-2 text-xs text-slate-500">Stato</div>
            <div className="font-bold text-sm uppercase text-slate-900 tracking-wide">{job.status.replace("_", " ")}</div>
          </div>
        </header>

        {/* Client */}
        <section className="mt-8 grid grid-cols-2 gap-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Cliente</div>
            <div className="font-display text-xl font-extrabold text-slate-900">{job.client_name}</div>
            {job.client_phone && (
              <div className="text-sm text-slate-700 mt-1 font-mono">{job.client_phone}</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Data e Orario</div>
            <div className="font-display text-xl font-extrabold text-slate-900">{job.scheduled_date}</div>
            <div className="text-sm text-slate-700 mt-1 font-mono">
              {job.start_time}{job.end_time && ` – ${job.end_time}`}
            </div>
            {job.depot_time && (
              <div className="mt-2 inline-flex items-center gap-2 bg-amber-100 border border-amber-300 rounded-lg px-3 py-1.5">
                <span className="text-base">🏭</span>
                <div className="leading-tight">
                  <div className="text-[9px] uppercase tracking-widest font-bold text-amber-700">In deposito alle</div>
                  <div className="font-mono font-extrabold text-amber-900">{job.depot_time}</div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Addresses */}
        <section className="mt-8 grid grid-cols-2 gap-6">
          <div className="border-l-4 border-sky-600 pl-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Partenza</div>
            <div className="text-slate-900 font-semibold leading-snug">{job.address_from}</div>
          </div>
          <div className="border-l-4 border-emerald-600 pl-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Arrivo</div>
            <div className="text-slate-900 font-semibold leading-snug">{job.address_to}</div>
          </div>
        </section>

        {/* Squadra */}
        <section className="mt-8">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Squadra Assegnata</div>
          <div className="flex flex-wrap gap-2">
            {(job.assigned_employees || []).length === 0 ? (
              <span className="text-sm italic text-slate-400">Nessuno</span>
            ) : (
              job.assigned_employees.map((e) => {
                const isForeman = job.foreman_id === e.id;
                return (
                  <span key={e.id} className={`${isForeman ? "bg-indigo-700" : "bg-slate-900"} text-white rounded-lg px-3 py-1.5 text-sm font-semibold`}>
                    {isForeman && "👷 "}
                    {e.name}
                    {isForeman && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold bg-white/20 rounded px-1.5 py-0.5">Caposquadra</span>}
                    {e.phone && <span className="font-mono text-xs text-slate-300 ml-2">{e.phone}</span>}
                  </span>
                );
              })
            )}
          </div>
        </section>

        {/* Materiali */}
        {job.materials && (
          <section className="mt-8">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">🧰 Materiali Necessari</div>
            <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-4 text-sm text-amber-900 whitespace-pre-line font-medium">
              {job.materials}
            </div>
          </section>
        )}

        {/* Items */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Oggetti da Traslocare</div>
            <div className="text-xs font-mono text-slate-500">{totalItems} pezzi totali</div>
          </div>
          <table className="w-full text-sm border border-slate-300">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 border-r border-slate-300 w-16">Qtà</th>
                <th className="text-left px-3 py-2 border-r border-slate-300">Oggetto</th>
                <th className="text-left px-3 py-2 border-r border-slate-300 w-28">Categoria</th>
                <th className="text-left px-3 py-2 border-r border-slate-300 w-32">Operazioni</th>
                <th className="text-left px-3 py-2 w-40">Note</th>
              </tr>
            </thead>
            <tbody>
              {job.items.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center italic text-slate-400">Nessun oggetto</td></tr>
              ) : (
                job.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-200">
                    <td className="px-3 py-2 font-mono font-bold text-sky-700 border-r border-slate-200">×{it.quantity}</td>
                    <td className="px-3 py-2 font-medium text-slate-900 border-r border-slate-200">{it.name}</td>
                    <td className="px-3 py-2 uppercase text-[10px] font-bold tracking-wider text-slate-500 border-r border-slate-200">{it.category || "altro"}</td>
                    <td className="px-3 py-2 border-r border-slate-200 text-[10px] font-bold tracking-wider">
                      {it.smontaggio && <span className="inline-block bg-indigo-100 text-indigo-800 rounded px-1.5 py-0.5 mr-1">🔧 SMONT.</span>}
                      {it.rimontaggio && <span className="inline-block bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">🔨 RIMONT.</span>}
                      {!it.smontaggio && !it.rimontaggio && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{it.note}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {/* Pricing */}
        <section className="mt-8">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Prezzo Concordato</div>
          <table className="w-full text-sm border border-slate-300">
            <tbody>
              {(job.total_amount != null && job.total_amount !== undefined) ? (
                <>
                  <tr className="border-b border-slate-200">
                    <td className="px-3 py-2 text-slate-600">Totale concordato</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">
                      {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.total_amount || 0))}
                    </td>
                  </tr>
                  {(() => {
                    const settle = Number(job.settle_amount || 0);
                    const total = Number(job.total_amount || 0);
                    const acconto = total - settle;
                    return (
                      <>
                        {acconto > 0 && (
                          <tr className="border-b border-slate-200">
                            <td className="px-3 py-2 text-slate-600">Acconto già incassato</td>
                            <td className="px-3 py-2 text-right font-mono text-slate-700">
                              −{new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(acconto)}
                            </td>
                          </tr>
                        )}
                        <tr className="bg-emerald-700 text-white">
                          <td className="px-3 py-2.5 font-display font-extrabold uppercase tracking-wider text-xs">Da Saldare al Termine</td>
                          <td className="px-3 py-2.5 text-right font-mono font-extrabold text-lg">
                            {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(settle)}
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </>
              ) : (
                <>
                  <tr className="border-b border-slate-200">
                    <td className="px-3 py-2 text-slate-600">Prezzo base</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">
                      {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.base_price || 0))}
                    </td>
                  </tr>
                  {(job.price_adjustments || []).map((a) => (
                    <tr key={a.id} className="border-b border-slate-200">
                      <td className="px-3 py-2 text-slate-600">{a.description}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${a.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {a.amount >= 0 ? "+" : ""}
                        {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(a.amount || 0))}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-900 text-white">
                    <td className="px-3 py-2.5 font-display font-extrabold uppercase tracking-wider text-xs">Totale</td>
                    <td className="px-3 py-2.5 text-right font-mono font-extrabold text-lg">
                      {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.final_price || 0))}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </section>

        {/* Notes */}
        {job.notes && (
          <section className="mt-8">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Note</div>
            <div className="border border-slate-300 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-line bg-slate-50">
              {job.notes}
            </div>
          </section>
        )}

        {/* Signatures */}
        <section className="mt-12 grid grid-cols-2 gap-8">
          <div>
            {job.employee_signature ? (
              <>
                <div className="h-14 flex items-end border-b border-slate-900">
                  <img src={job.employee_signature} alt="Firma dipendente" className="max-h-14 object-contain" />
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-2">
                  Firma Responsabile Squadra — {job.employee_signer_name || "—"}
                </div>
                {job.employee_signed_at && (
                  <div className="text-[10px] text-slate-400 font-mono mt-1">
                    Firmato il {new Date(job.employee_signed_at).toLocaleString("it-IT")}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="border-b border-slate-900 h-14" />
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-2">Firma Responsabile Squadra</div>
              </>
            )}
          </div>
          <div>
            {job.client_signature ? (
              <>
                <div className="h-14 flex items-end border-b border-slate-900">
                  <img src={job.client_signature} alt="Firma cliente" className="max-h-14 object-contain" />
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-2">
                  Firma Cliente — {job.client_signer_name || job.client_name}
                </div>
                {job.signed_at && (
                  <div className="text-[10px] text-slate-400 font-mono mt-1">
                    Firmato il {new Date(job.signed_at).toLocaleString("it-IT")}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="border-b border-slate-900 h-14" />
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-2">Firma Cliente</div>
              </>
            )}
          </div>
        </section>

        <footer className="mt-10 pt-6 border-t border-slate-200 text-[11px] text-slate-400 font-mono flex items-center justify-between">
          <span>In & Out Traslochi — Gestionale Operativo</span>
          <span>Generato il {new Date().toLocaleString("it-IT")}</span>
        </footer>
      </div>
    </div>
  );
}
